import React from 'react';
import { createUseStyles } from 'react-jss';

import { useLocale, useT } from '../i18n';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * Which language the game speaks — a per-device preference, like the layout
 * plates it sits beside, so it never touches the socket.
 *
 * **A native `<select>`, deliberately.** This was a grid of plates, one per
 * language, which is a fine control for two and an unusable one for eight:
 * the plates wrap into a block that dwarfs the panel holding it, and the
 * tutorial header — which caps this at 260px — has nowhere to put them. A
 * select is the only option here whose size is constant in the number of
 * languages.
 *
 * The rest is what a native control gives for free and a custom listbox would
 * have to earn back, in code, per platform: keyboard selection and type-ahead,
 * a correct screen-reader role, the iOS wheel and the Android dialog on touch,
 * and OS-native rendering of scripts our own CSS has never been tested
 * against. It also stays out of `useModalEscape`'s LIFO stack — the browser
 * closes its own popup on ESC without the modal underneath hearing it.
 *
 * Every language is offered under its own name. Somebody opening this control
 * is, by definition, the person who cannot read the language it is currently
 * in — so the control carries no prose, and the one string it does use is an
 * `aria-label`.
 */
const useStyles = createUseStyles({
  wrap: {
    position: 'relative',
    display: 'block',
    minWidth: 0,
  },
  // A recessed plate, like every other input in this game: pressed inward,
  // focus shown by shifting borderColor rather than an outline ring.
  select: {
    width: '100%',
    minHeight: '44px',
    // Room for the chevron, which is painted over the top.
    padding: '10px 34px 10px 12px',
    background: '#2a1a0e',
    border: '1px solid #4a3020',
    borderRadius: '7px',
    color: '#f0dcc0',
    cursor: 'pointer',
    fontFamily: MONO,
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
    // The native arrow is drawn in the OS's own colours and cannot be themed;
    // ours is the span below.
    appearance: 'none',
    MozAppearance: 'none',
    WebkitAppearance: 'none',
    transition: 'border-color .12s ease, color .12s ease',
    '&:hover': { borderColor: '#6b4622', color: '#f5e6c8' },
    '&:focus': { outline: 'none', borderColor: '#e0a325' },
    '&:focus-visible': { outline: 'none', borderColor: '#e0a325' },
  },
  // The popup is drawn by the OS, not by us. These two are the only
  // properties it reliably honours, and without them a dark-theme Firefox or
  // Chrome on Linux renders the list light-on-light.
  option: {
    background: '#2a1d13',
    color: '#f0dcc0',
  },
  chevron: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    color: '#c4a070',
    pointerEvents: 'none',
  },
});

function LanguagePicker() {
  const classes = useStyles();
  const t = useT();
  const { locale, setLocale, locales } = useLocale();

  return (
    <div className={classes.wrap}>
      <select
        className={classes.select}
        value={locale}
        lang={locale}
        aria-label={t('a11y.language')}
        onChange={e => setLocale(e.target.value)}
      >
        {locales.map(({ code, name }) => (
          // `lang` per option so a screen reader pronounces each name in its
          // own language rather than reading Ελληνικά as English.
          <option key={code} value={code} lang={code} className={classes.option}>
            {name}
          </option>
        ))}
      </select>
      <span className={classes.chevron} aria-hidden="true">
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3.5 6L8 10.5L12.5 6" />
        </svg>
      </span>
    </div>
  );
}

export default LanguagePicker;
